import { useId } from 'react';
import type { ReactElement } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { useGetMortgagePolicyEntitiesQuery } from '../api/mortgageEntities.api';
import type { MortgagePreEvaluationConfig, MortgageTopology } from '../model/mortgageRules';

interface Props {
  countryCode: string | null | undefined;
  value: MortgagePreEvaluationConfig;
  disabled: boolean;
  /** The flag reaches both topologies, but not through the same doors — see MULTI_AGENT_COPY. */
  topology: MortgageTopology;
  onChange: (next: MortgagePreEvaluationConfig) => void;
}

/*
 * Temporary either way: the flag exists while projects finish migrating. What it governs differs by
 * topology. With sub-projects it is the split itself, the conversation divided between sales, advisor
 * and brokerage. Without them the project answers with one agent regardless, and the flag only decides
 * whether that agent keeps the retired simulation tools and which sections reach its prompt.
 */
const MULTI_AGENT_COPY: Record<MortgageTopology, string> = {
  subProjects:
    'Temporal, mientras quedan proyectos por migrar. Desactivado, el modo hipotecario corre como antes: ventas y el ' +
    'agente hipotecario antiguo, sin asesor financiero de por medio. Debe quedar activado en prácticamente todos ' +
    'los casos.',
  leadManagement:
    'Temporal, mientras quedan proyectos por migrar. Este proyecto atiende con un solo agente de todas formas, así ' +
    'que el flag no divide la conversación: decide si ese agente conserva las herramientas de simulación antiguas y ' +
    'qué secciones entran a su prompt.',
};

/* The controlled half of MORTGAGE_PRE_EVALUATION_CONFIG, shared by the single-project card and the
   bulk section, which write it to one project and to many. */
const MortgagePreEvaluationFields = ({ countryCode, value, disabled, topology, onChange }: Props): ReactElement => {
  const { data: entities = [], isError } = useGetMortgagePolicyEntitiesQuery();
  /* One card per project can share a page (the products of a financial entity), so the ids are per instance. */
  const idPrefix = useId();

  /* Stored codes outside the project's country stay on the list so saving never drops one silently. */
  const available = entities.filter((entity) => entity.isActive && entity.countryCode === countryCode);
  const options = [
    ...available,
    ...value.entityCodes
      .filter((code) => !available.some((entity) => entity.code === code))
      .map((code) => ({ id: -1, code, name: code, countryCode: '', isActive: true, projectId: null })),
  ];

  const toggleCode = (code: string, checked: boolean): void => {
    onChange({
      ...value,
      entityCodes: checked ? [...value.entityCodes, code] : value.entityCodes.filter((item) => item !== code),
    });
  };

  return (
    <div className="space-y-4">
      {/* The catalog lives under /admin/mortgagePolicyEntities, which sits behind a stricter permission
          than this screen. Failing silently would render an empty list that reads as "no entities". */}
      {isError && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
          No se pudo cargar el catálogo de entidades financieras. Puede que tu rol no tenga acceso a esa configuración.
        </p>
      )}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {options.map((entity) => (
          <div key={entity.code} className="flex items-center gap-2 rounded-md border border-border p-3">
            <Checkbox
              id={`${idPrefix}-entity-${entity.code}`}
              checked={value.entityCodes.includes(entity.code)}
              disabled={disabled}
              onCheckedChange={(checked) => toggleCode(entity.code, checked === true)}
            />
            <Label htmlFor={`${idPrefix}-entity-${entity.code}`} className="text-sm font-normal">
              {entity.name} <span className="text-text-muted">({entity.code})</span>
            </Label>
          </div>
        ))}
      </div>

      {/* An entity's estimates choose by subsidy among the marked entities that belong to it, so a product that
          marks another product's entity would estimate with its rates. */}
      {topology === 'leadManagement' && (
        <p className="text-xs text-text-muted">
          Las estimaciones eligen por subsidio entre las entidades marcadas que sean de esta financiera. En cada
          producto, marca solo las suyas.
        </p>
      )}

      <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
        <div className="space-y-1 pr-2">
          <Label htmlFor={`${idPrefix}-demo-toggle`} className="text-sm font-medium">
            Proyecto demo
          </Label>
          <p className="text-xs text-text-muted">
            Para demos y pruebas internas. Activado, los clientes de este proyecto no se derivan a las entidades
            financieras que acepten. El resto del flujo hipotecario corre igual.
          </p>
        </div>
        <Switch
          id={`${idPrefix}-demo-toggle`}
          checked={value.isDemo}
          disabled={disabled}
          onCheckedChange={(isDemo) => onChange({ ...value, isDemo })}
        />
      </div>

      {topology === 'subProjects' && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
          <div className="space-y-1 pr-2">
            <Label htmlFor="capacity-pdf-toggle" className="text-sm font-medium">
              Enviar PDF de capacidad
            </Label>
            <p className="text-xs text-text-muted">
              Al estimar la capacidad de financiamiento, el agente envía un PDF referencial con el detalle por plazo.
              Desactivado, entrega las mismas cifras y consejos en la conversación, sin documento.
            </p>
          </div>
          <Switch
            id="capacity-pdf-toggle"
            checked={value.sendsCapacityPdf}
            disabled={disabled}
            onCheckedChange={(sendsCapacityPdf) => onChange({ ...value, sendsCapacityPdf })}
          />
        </div>
      )}

      <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
        <div className="space-y-1 pr-2">
          <Label htmlFor={`${idPrefix}-multi-agent-toggle`} className="text-sm font-medium">
            Múltiples agentes
          </Label>
          <p className="text-xs text-text-muted">{MULTI_AGENT_COPY[topology]}</p>
        </div>
        <Switch
          id={`${idPrefix}-multi-agent-toggle`}
          checked={value.isMultiAgent}
          disabled={disabled}
          onCheckedChange={(isMultiAgent) => onChange({ ...value, isMultiAgent })}
        />
      </div>
    </div>
  );
};

export default MortgagePreEvaluationFields;
